<template>
  <nav
    class="h-full overflow-y-auto transition-transform transform bg-white duration-light lg:transform-none border-r border-solid border-base-300"
    :class="{
      '-translate-x-full': !isNavOpen,
      'translate-x-0': isNavOpen,
    }"
  >
    <div class="flex flex-col max-w-full min-h-full p-2">
      <div class="relative flex items-center gap-3 p-2">
        <BambeeLogo class="flex-shrink-0" />
        <CompanySelect
          class="flex-grow"
          :active="companyId"
          :companies="allCompanies"
          :light="false"
          @input="handleCompanyChange"
        />
      </div>
      <ul class="flex flex-col gap-1">
        <li
          v-for="{
            label,
            icon,
            to,
            handler,
            tag,
            key,
            tagColor = 'caribbean',
            preventDelinquent,
            activeSubLabel,
            isActive,
            isAi,
            ...rest
          } in navItems"
          :key="key || icon"
          v-bind="rest"
          class="flex -mx-2"
        >
          <NavItem
            class="flex-grow"
            :handler="handler"
            :icon="icon"
            :to="to"
            :locked="false"
            :is-ai="isAi"
            locked-tooltip="Update your payment to gain access"
          >
            <div>
              <div>
                {{ label }}
              </div>
              <div
                v-if="activeSubLabel && isActive"
                class="font-normal text-base-500 text-11"
              >
                {{ activeSubLabel }}
              </div>
            </div>
            <CounterBadge
              v-if="incompleteTasksCount && key === NavItemKeys.DASHBOARD"
              :value="incompleteTasksCount"
              variant="secondary"
              class="ml-auto"
            />
            <BaseTag v-if="tag" :color="tagColor" size="small" class="ml-auto">
              {{ tag }}
            </BaseTag>
            <div v-if="isAi" class="ml-auto">
              <NewTag />
            </div>
          </NavItem>
        </li>
      </ul>

      <div class="mt-auto">
        <!-- Feature flag 'display-hr-requests-in-left-nav' removed - was always FALSE in production -->
        <UserSubNav class="-mx-2" />
      </div>
    </div>
  </nav>
</template>

<script>
import { BaseTag, CounterBadge } from '@bambeehr/pollen';
import BambeeLogo from '@/components/BambeeLogo/BambeeLogo.vue';
import launchDarkly from '@bambeehr/vue-launch-darkly';
import get from 'lodash/get';
import { mapActions, mapGetters, mapState } from 'vuex';
import CompanySelect from '@/components/default-layout/CompanySelect';
import NavItem from '@/components/default-layout/NavItem';
import FeatureFlags from '@/constants/FeatureFlags';
import Store from '@/constants/Store';
import bam from '@/lib/bam';
import { CABINET, DASHBOARD } from '@/lib/globals/routes';
import NewTag from '@/components/NewTag.vue';

import { canAccessTrainingModule } from '@/modules/Training';
import {
  canAccessVoices,
  getEmployeeVoicesState,
} from '@/modules/EmployeeVoices/helpers/canAccessVoices';
import {
  computed,
  defineComponent,
  useStore,
  ref,
} from '@nuxtjs/composition-api';
import useFeeds from '@/modules/TaskCenter/hooks/useFeeds';
import isTrainingEmployerRole from '@/modules/Training/helpers/isTrainingEmployerRole';
import useTierZeroTasks from '@/modules/TaskCenter/hooks/useTierZeroTasks/useTierZeroTasks';
import useIntercom from '@/hooks/useIntercom/useIntercom';
import useGoals from '@/modules/TaskCenter/hooks/useGoals/useGoals';
import usePlanAccess from '@/hooks/usePlanAccess/usePlanAccess';
import useCompanyStore from '@/store/companies';

const UserSubNav = () => import('@/components/default-layout/UserSubNav');

const NavItemKeys = Object.freeze({
  DASHBOARD: 'dashboard',
  DASHBOARD_HOTLINE: 'dashboard-hotline',
  OPERATING_FEED: 'operating-feed',
  SOLUTIONS: 'solutions',
  JOURNAL: 'journal',
  REQUESTS: 'requests',
  GUIDANCE: 'guidance',
  CONVERSATIONS: 'conversations',
  STAFF: 'staff',
  HR_POLICY: 'hr-policy',
  CABINET: 'cabinet',
  TRAINING_PORTAL: 'training-portal',
  REPORT_CARDS: 'report-cards',
  VOICES: 'voices',
  INSURANCE: 'insurance',
  PERFORMANCE_REVIEWS: 'performance-reviews',
  MEETINGS: 'meetings',
});

export default defineComponent({
  components: {
    BaseTag,
    CompanySelect,
    CounterBadge,
    NavItem,
    UserSubNav,
    BambeeLogo,
    NewTag,
  },
  props: {
    isNavOpen: {
      type: Boolean,
      default: false,
    },
  },
  setup() {
    const { totalFeedCount } = useFeeds();
    const { incompleteTasksCount: tierZeroTaskCount } = useTierZeroTasks();
    const { shouldShowNewMessageBadge, unreadMessagesCount } = useIntercom();
    const { incompleteGoals, setCurrentGoal } = useGoals();
    const { planAccessMap, isBambeeLite, hasBasicAccess } = usePlanAccess();
    const incompleteTasksCount = computed(() => {
      if (tierZeroTaskCount.value) {
        return tierZeroTaskCount.value + totalFeedCount.value;
      }

      return totalFeedCount.value + incompleteGoals.value.length;
    });

    return {
      unreadMessagesCount,
      shouldShowNewMessageBadge,
      totalFeedCount,
      incompleteTasksCount,
      setCurrentGoal,
      DASHBOARD,
      NavItemKeys,
      planAccessMap,
      isBambeeLite,
      hasBasicAccess,
    };
  },

  data() {
    return {
      showTraining: false,
      showVoices: false,
    };
  },
  computed: {
    ...mapGetters({
      companyId: 'companyId',
      isCompanyAdmin: Store.auth.Getters.IS_COMPANY_ADMIN,
      isEmployee: Store.auth.Getters.IS_EMPLOYEE,
      isManager: Store.auth.Getters.IS_MANAGER,
      isPolicyApprover: Store.auth.Getters.IS_POLICY_APPROVER,
      isSubscribed: Store.auth.Getters.IS_SUBSCRIBED,
      isLongStandingDelinquent: Store.auth.Getters.IS_LONG_STANDING_DELINQUENT,
    }),
    ...mapState({
      currentUser: 'current_user',
    }),
    allCompanies() {
      const companyStore = useCompanyStore();

      return companyStore.all;
    },
    showEmployerBenefitsUpsell() {
      return false;
    },
    showEmployerBenefits() {
      return false;
    },
    showStaffBenefits() {
      return false;
    },
    // Feature flags
    isHrRequestInLeftNav() {
      // Feature flag 'display-hr-requests-in-left-nav' removed - was always FALSE in production
      return false;
    },
    canSeeMeetings() {
      return (
        launchDarkly.getFlags()[FeatureFlags.MEETINGS_FEATURE] &&
        !this.isBambeeLite
      );
    },
    lockLongStandingDelinquent() {
      // Feature flag 'lock-long-standing-delinquents' removed - was always FALSE in production
      return false;
    },
    isH3FlywheelEnabled() {
      return launchDarkly.getFlags()[FeatureFlags.H3_FLYWHEEL];
    },
    isEmployeeManagerOperatingFeedEnabled() {
      // Feature flag 'employee-manager-operating-feed' removed - was always FALSE in production
      return false;
    },
    isJournalingEnabled() {
      return launchDarkly.getFlags()[FeatureFlags.H3_FLYWHEEL];
    },

    navItems() {
      const sortOrder = [
        NavItemKeys.DASHBOARD,
        NavItemKeys.OPERATING_POSTURE,
        NavItemKeys.SOLUTIONS,
        NavItemKeys.JOURNAL,
        NavItemKeys.REQUESTS,
        NavItemKeys.CONVERSATIONS,
        NavItemKeys.MEETINGS,
        NavItemKeys.STAFF,
        NavItemKeys.HR_POLICY,
        NavItemKeys.CABINET,
        NavItemKeys.TRAINING_PORTAL,
        NavItemKeys.REPORT_CARDS,
        NavItemKeys.VOICES,

        NavItemKeys.INSURANCE,
      ];
      const bySortOrder = (a, b) => {
        const aIndex = sortOrder.indexOf(a.key);
        const bIndex = sortOrder.indexOf(b.key);

        // In the case of missing to add a key to the sort order push items without a sort order to the end
        const isSortOrderDefined = aIndex !== -1 && bIndex !== -1;
        if (!isSortOrderDefined || aIndex > bIndex) {
          return 1;
        }

        if (aIndex < bIndex) {
          return -1;
        }

        return 0;
      };

      return [
        !this.isSubscribed && {
          label: 'About Bambee',
          key: NavItemKeys.ABOUT_BAMBEE,
          icon: 'lightbulb',
          to: '/about-bambee',
        },
        // Show dashboard when h3 flywheel is disabled OR when user is not a company admin
        // Also show for employees/managers when employee-manager-operating-feed flag is enabled (for policy review)
        (!this.isH3FlywheelEnabled || !this.isCompanyAdmin) &&
          this.planAccessMap?.dashboard?.showInNav &&
          this.isSubscribed && {
            label: DASHBOARD.label,
            key: NavItemKeys.DASHBOARD,
            icon: 'checkCircle',
            handler: () => {
              this.setCurrentGoal();
            },
            to: DASHBOARD.path,
          },

        // Show operating feed when h3 flywheel is enabled (for admins, employees, and managers)
        this.isH3FlywheelEnabled &&
          (this.isCompanyAdmin || this.isEmployee || this.isManager) && {
            label: 'Operating Feed',
            key: NavItemKeys.OPERATING_FEED,
            icon: 'shield',
            to: '/operating-feed',
            isAi: true,
          },

        this.isH3FlywheelEnabled &&
          this.isCompanyAdmin &&
          !this.isBambeeLite && {
            label: 'Solutions',
            key: NavItemKeys.SOLUTIONS,
            icon: 'circle',
            to: '/solutions',
            isAi: true,
          },

        {
          label: 'Journal',
          key: NavItemKeys.JOURNAL,
          icon: 'clipboardCheck',
          to: '/journal',
          isAi: true,
        },

        // Show hotline dashboard when h3 flywheel is disabled
        !this.isH3FlywheelEnabled &&
          this.planAccessMap?.hotline?.showInNav &&
          this.isSubscribed && {
            label: DASHBOARD.label,
            key: NavItemKeys.DASHBOARD_HOTLINE,
            icon: 'checkCircle',
            to: '/dashboard/lite',
          },

        this.isSubscribed &&
          !this.isHrRequestInLeftNav &&
          this.hasBasicAccess && {
            label: 'HR Guidance',
            key: NavItemKeys.GUIDANCE,
            icon: 'request',
            to: '/guidance',
            preventDelinquent: true,
          },
        // This is the "old" conversations / intercom nav item
        // We will be renaming this nav item
        this.hasBasicAccess &&
          this.isSubscribed && {
            label: 'Conversations',
            key: NavItemKeys.CONVERSATIONS,
            icon: 'comment',
            handler: () => {
              try {
                Intercom('showMessages');
              } catch (err) {
                window.DD_RUM.addError('INTERCOM_FAILED_SHOW', {
                  err,
                });
              }
              bam.trackEvent('button_clicked', {
                location: 'nav-conversations',
                action: 'open-conversations',
              });
            },
            tag: this.shouldShowNewMessageBadge
              ? `${this.unreadMessagesCount || 1}+`
              : '',
            tagColor: 'tangerine',
            preventDelinquent: true,
          },
        this.canSeeMeetings &&
          this.isManager && {
            label: 'Meetings',
            key: NavItemKeys.MEETINGS,
            icon: 'users',
            to: '/meetings',
            preventDelinquent: true,
          },
        // this.planAccessMap?.conversations?.showInNav &&
        //   this.isBambeeLite && {
        //     label: 'Conversations',
        //     key: NavItemKeys.CONVERSATIONS,
        //     icon: 'comment',
        //     to: '/conversations',
        //     preventDelinquent: true,
        //   },
        this.planAccessMap?.policies?.showInNav &&
          this.isPolicyApprover && {
            label: 'HR Policy',
            key: NavItemKeys.HR_POLICY,
            icon: 'clipboardCheck',
            to: this.isSubscribed ? '/policy' : '/about-bambee/about-policies',
            preventDelinquent: true,
          },
        // Currently showing to all admins
        // Only showing to EEs if they are enrolled
        // We will be updating this soon to show to all EEs
        this.planAccessMap?.training?.showInNav &&
          ((this.showTraining && this.isEmployee) || !this.isEmployee) && {
            label: 'Training',
            key: NavItemKeys.TRAINING_PORTAL,
            icon: 'usersClass',
            to: isTrainingEmployerRole(this.$store)
              ? '/training'
              : '/my-training',
          },
        this.planAccessMap?.staff?.showInNav &&
          this.isManager && {
            label: 'Staff',
            key: NavItemKeys.STAFF,
            icon: 'user',
            to: this.isSubscribed ? '/employee' : '/about-bambee/about-folders',
            preventDelinquent: true,
          },
        this.planAccessMap?.reportCards?.showInNav &&
          this.showReportCards && {
            label: 'Report Cards',
            key: NavItemKeys.REPORT_CARDS,
            icon: 'ballotCheck',
            to: (() => {
              if (this.isSubscribed) {
                if (this.planAccessMap?.reportCards?.isUpsell) {
                  return '/upgrade/report-cards';
                }

                return '/report-cards';
              }

              return '/about-bambee/about-reportcards';
            })(),
            preventDelinquent: true,
          },
        this.planAccessMap?.voices?.showInNav &&
          this.showVoices &&
          this.isSubscribed && {
            label: 'Voices',
            key: NavItemKeys.VOICES,
            icon: 'megaphone',
            to: this.planAccessMap?.voices?.isUpsell
              ? '/upgrade/voices'
              : '/voices',
            preventDelinquent: true,
          },
        // TODO: figure out logic for showReviews
        this.showReviews && {
          label: 'Performance Reviews',
          key: NavItemKeys.PERFORMANCE,
          icon: 'analytics',
          to: this.isSubscribed
            ? '/employee-review'
            : '/about-bambee/about-employee-review',
          preventDelinquent: true,
        },
        this.planAccessMap?.cabinet?.showInNav && {
          label: 'Cabinet',
          key: NavItemKeys.CABINET,
          icon: 'cabinetFiling',
          to: this.isSubscribed ? CABINET : '/about-bambee/about-cabinet',
        },
      ]
        .filter(Boolean)
        .map((item) => ({
          ...item,
          isActive: item.to && this.$route.path.includes(item.to),
        }))
        .sort(bySortOrder);
    },

    showInsurance() {
      const company = this.currentUser._company;

      return this.isEmployee
        ? company.settings.personal_insurance_enabled
        : company.settings.insurance_enabled;
    },

    showReportCards() {
      const settings = get(this.currentUser, '_company.settings', {});
      const { bciToHRPracticeMigrated, show_report_card: showReportCard } =
        settings;

      // SHOW IF COMPANY WAS MIGRATED FROM BCI TO HR PRACTICE
      if (bciToHRPracticeMigrated) {
        return true;
      }

      // HIDE IF COMPANY DOESN'T ALLOW REPORT CARDS
      return !!showReportCard;
    },
    showReviews() {
      const settings = get(this.currentUser, '_company.settings', {});
      const {
        show_employee_review: showEmployeeReview,
        employee_review: employeeReview,
      } = settings;

      if (!showEmployeeReview) {
        return false;
      }
      if (this.isCompanyAdmin) {
        return true;
      }
      if (this.isManager && employeeReview) {
        return true;
      }

      return false;
    },
  },
  methods: {
    ...mapActions({
      fetchFeeds: 'feeds/fetchFeeds',
      switchCompany: Store.auth.Actions.SWITCH_COMPANY,
    }),
    async runAsyncGuards() {
      this.showTraining = await this.canAccessTraining();
      const voicesManuallyDisabled = await getEmployeeVoicesState(
        this.companyId
      );
      this.showVoices =
        (await canAccessVoices(this.companyId)) &&
        !voicesManuallyDisabled.isManuallyDisabled;
    },
    async canAccessTraining() {
      const { getters } = this.$store;
      // eslint-disable-next-line no-underscore-dangle
      const userId = getters?.currentUser._id;
      const canAccess = await canAccessTrainingModule(userId, this.$store);

      return canAccess;
    },
    async handleCompanyChange(companyId) {
      try {
        await this.switchCompany(companyId);
      } catch (err) {
        this.$toast.open({
          type: 'is-danger',
          position: 'is-bottom',
          message: err,
        });
      }
    },
  },
  async created() {
    await this.runAsyncGuards();
    if (this.$route.path !== '/task-center') {
      this.fetchFeeds();
    }
  },
});
</script>

<style scoped>
.company-select {
  /* 3.25rem is the width and margin of the logo. */
  max-width: calc(100% - 3.25rem);
}

.counter-badge {
  @apply rounded-sm;
}
</style>
